import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { Button, Input } from "../ui";
import { createTag } from "../../store/features/ownerActions";
import { ownerSelector, RootState } from "../../store/store";
import { setTags } from "../../store/slices/ownerSlice";
import { Tag } from "../../types";
import { MdOutlineColorize } from "react-icons/md";
import { resolveDashboardScope } from "../../utils";

type CreateTagModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function normalizeHex(value: string): string | null {
  const raw = value.trim().replace(/^#/, "");
  if (!/^[0-9A-Fa-f]{6}$/.test(raw)) return null;
  return `#${raw.toLowerCase()}`;
}

const normalizeIdString = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value !== null && "_id" in value) {
    return String((value as { _id?: unknown })._id || "").trim();
  }
  return String(value).trim();
};

const CreateTagModal: React.FC<CreateTagModalProps> = ({ isOpen, onClose }) => {
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const { owner, tags } = useSelector(ownerSelector);
  const [tagName, setTagName] = useState("");
  const [hexDigits, setHexDigits] = useState("74a567");
  const [saving, setSaving] = useState(false);
  const colorInputRef = React.useRef<HTMLInputElement>(null);

  const hexForPicker = normalizeHex(hexDigits) || "#74a567";

  React.useEffect(() => {
    if (!isOpen) {
      setTagName("");
      setHexDigits("74a567");
      setSaving(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const name = tagName.trim();
    if (!name) {
      toast.error("Tag name is required");
      return;
    }
    const color = normalizeHex(hexDigits);
    if (!color) {
      toast.error("Enter a valid 6-digit hex color");
      return;
    }
    const ownerIdHint = normalizeIdString(owner?._id as unknown);
    const userIdHint = normalizeIdString((owner as { userId?: unknown })?.userId);
    const workspaceIdHint = normalizeIdString(
      (owner as { workspaces?: Array<{ _id?: unknown }> })?.workspaces?.[0]?._id
    );
    const { resolvedUserId, resolvedWorkspaceId } = resolveDashboardScope(
      userIdHint || undefined,
      workspaceIdHint || undefined
    );

    const hasScope =
      Boolean(ownerIdHint) || Boolean(resolvedUserId) || Boolean(resolvedWorkspaceId);
    if (!hasScope) {
      toast.error("You must be signed in to create tags");
      return;
    }

    setSaving(true);
    try {
      const res = await dispatch(
        createTag({
          tagName: name,
          ownerId: ownerIdHint || undefined,
          userId: resolvedUserId,
          workspaceId: resolvedWorkspaceId,
          color,
        })
      );
      if (res.meta.requestStatus === "fulfilled" && (res.payload as { success?: boolean })?.success) {
        const payload = res.payload as { tag?: Tag; message?: string };
        if (payload.tag) {
          dispatch(setTags([...tags, payload.tag]));
        }
        toast.success(payload.message || "Tag created");
        onClose();
      } else if (res.meta.requestStatus === "fulfilled") {
        toast.error((res.payload as { message?: string })?.message || "Could not create tag");
      } else {
        toast.error("Could not create tag");
      }
    } catch {
      toast.error("Could not create tag");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-[420px] rounded-[10px] border border-light-grey bg-white shadow-lg">
        <div className="relative border-b border-light-grey px-5 py-4">
          <h2 className="pr-10 text-left text-md font-semibold text-dark-black">Create Tags</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-light-grey text-dark-grey hover:bg-primary"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>
        <div className="px-5 py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <Input
                label="Tag name *"
                size="sm"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Important"
                autoComplete="off"
              />
            </div>
            <div className="w-full shrink-0 sm:w-[120px]">
              <label className="mb-2 block text-sm font-medium text-black" htmlFor="tag-color-hex">
                Select Color *
              </label>
              <div className="relative">
                <input
                  id="tag-color-hex"
                  value={hexDigits}
                  onChange={(e) => setHexDigits(e.target.value.replace(/#/g, "").slice(0, 6))}
                  className="h-10 w-full rounded-[8px] border border-[#E1E1E1] bg-white pr-10 pl-3 text-xs uppercase text-dark-black outline-none transition-colors focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
                  placeholder="FF0000"
                  spellCheck={false}
                />
                <input
                  ref={colorInputRef}
                  type="color"
                  value={hexForPicker}
                  onChange={(e) => setHexDigits(e.target.value.replace(/^#/, ""))}
                  className="sr-only"
                  aria-label="Pick tag color"
                />
                <button
                  type="button"
                  aria-label="Open color picker"
                  onClick={() => colorInputRef.current?.click()}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[6px] text-dark-grey hover:bg-primary"
                >
                  <MdOutlineColorize size={18} />
                </button>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            fullWidth
            className="mt-6 font-medium text-dark-black"
            isDisabled={saving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateTagModal;
